import { useCallback, useEffect, useState } from "react";
import { Layers } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { formatDate } from "@/lib/format";

// Lote 95 — audiências/prazos lançados mais de uma vez (mesmo processo, data, tipo e hora).
// O DJEN publica uma comunicação por ato e por polo; antes do Lote 95 cada uma virava um
// compromisso próprio. A partir de agora o app não duplica mais; este aviso limpa o que já
// existia: mantém um registro por ato e mescla os demais nele (com prévia e confirmação).

const TIPO = { audiencia: "Audiência", prazo: "Prazo", reuniao: "Reunião" };
const ORIGEM = { djen: "Diário (DJEN)", datajud: "DataJud", manual: "Manual", djen_auto_prazo: "Prazo automático" };

function dia(iso) {
  return iso ? formatDate(iso) : "";
}

export default function DuplicadosBanner({ onMerged }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => http.get("/publicacoes/duplicados").then((r) => setData(r.data)).catch(() => {}),
    [],
  );
  useEffect(() => { load(); }, [load]);

  if (!data || !data.total_groups) return null;
  const extras = data.total_extras;

  const merge = async () => {
    setBusy(true);
    try {
      const r = await http.post("/publicacoes/duplicados/mesclar", { keys: null });
      const n = r.data?.merged || 0;
      toast.success(`${n} ${n === 1 ? "registro repetido mesclado" : "registros repetidos mesclados"}.`);
      setOpen(false);
      await load();
      if (onMerged) onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível mesclar agora. Tente de novo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        data-testid="duplicados-banner"
        className="mb-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 flex items-start gap-3"
      >
        <Layers className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" strokeWidth={2} />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold">
            {extras} {extras === 1 ? "compromisso repetido" : "compromissos repetidos"} na agenda
          </p>
          <p className="text-[13px] mt-0.5 text-amber-900/90">
            O mesmo ato foi lançado mais de uma vez (o Diário publica uma comunicação para cada parte). Revise e mescle: fica um só por processo, data e hora.
          </p>
        </div>
        <Button
          type="button" size="sm" variant="outline" data-testid="duplicados-revisar"
          className="shrink-0 border-amber-300 bg-white/70 hover:bg-white text-amber-900"
          onClick={() => setOpen(true)}
        >
          Revisar
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="duplicados-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Mesclar compromissos repetidos</DialogTitle>
            <DialogDescription>
              Em cada grupo, o primeiro registro é mantido e os demais são mesclados nele: o evento repetido sai do Google Agenda, o lembrete "Baixar ata" repetido é dispensado e as publicações ficam ligadas ao registro mantido. Nada é apagado do histórico.
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-3" data-testid="duplicados-lista">
            {data.groups.map((g) => (
              <li key={g.key} className="rounded-lg border border-stone-200 bg-white px-3.5 py-3 text-sm">
                <div className="font-semibold text-stone-900">
                  {TIPO[g.event_type] || "Compromisso"} · {dia(g.date)}{g.time && g.time !== "00:00" ? ` às ${g.time}` : ""}
                </div>
                <div className="text-[13px] text-stone-600 mt-0.5">
                  {g.case_number}{g.parties ? ` · ${g.parties}` : ""}
                </div>
                <div className="mt-2 text-[12px] text-stone-600 space-y-0.5">
                  <div>
                    <span className="font-semibold text-emerald-700">Mantém:</span>{" "}
                    {ORIGEM[g.keeper.source] || g.keeper.source}
                    {g.keeper.validated ? " · validado" : ""}
                    {g.keeper.protected ? " · compartilhado" : ""}
                  </div>
                  {g.extras.map((e) => (
                    <div key={e.pub_id}>
                      <span className="font-semibold text-amber-700">{e.protected ? "Não mescla (compartilhado):" : "Mescla:"}</span>{" "}
                      {ORIGEM[e.source] || e.source}{e.validated ? " · validado" : ""}
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>Agora não</Button>
            <Button
              type="button" data-testid="duplicados-mesclar" disabled={busy || !extras} onClick={merge}
              className="bg-emerald-brand hover:bg-emerald-brand/90 text-white"
            >
              {busy ? "Mesclando…" : `Mesclar ${extras} ${extras === 1 ? "repetido" : "repetidos"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
